import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Printer, ArrowLeft } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatINR, formatDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

/* ───────────────────────────────────────────────────────────────
   REEDOS Premium Credit Note — print stylesheet
   - Mirrors proposal/invoice system (same tokens + sections)
   - Distinguished by rose 'CREDIT NOTE' tag and credit banner
   ─────────────────────────────────────────────────────────────── */
const printStyles = `
:root {
  --reedos-blue: #1A237E;
  --reedos-orange: #E8472C;
  --rose: #BE123C;
  --rose-bg: #FFF1F2;
  --ink: #111827;
  --muted: #4B5563;
  --light: #6B7280;
  --rule: #E5E7EB;
  --rule-strong: #D1D5DB;
  --table-header: #F8FAFC;
  --card-bg: #FAFAFA;
}

* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

@page {
  size: A4;
  margin: 16mm 14mm 24mm 14mm;
  @bottom-left {
    content: "REEDOS Software Solutions Private Limited  ·  GSTIN 27AAJCR1173P1ZT  ·  CREDIT NOTE";
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 7pt;
    color: #6B7280;
    letter-spacing: 0.02em;
  }
  @bottom-right {
    content: "Page " counter(page) " of " counter(pages);
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 7pt;
    color: #6B7280;
    letter-spacing: 0.06em;
  }
}

@media print {
  [data-testid="app-layout"] > aside,
  [data-testid="app-header"],
  [data-testid="app-sidebar"],
  aside,
  nav[aria-label="Sidebar"],
  /* 2026-02-10 REEDOS branding compliance: removed legacy
     third-party-badge hide-rules; the loader that injected those
     elements no longer ships in any build. */
  [data-sonner-toaster],
  [cmdk-overlay],
  [cmdk-root],
  .no-print { display: none !important; }

  body, html { background: white !important; margin: 0 !important; padding: 0 !important; }
  [data-testid="app-layout"] { display: block !important; }
  [data-testid="app-layout"] > main { display: block !important; }

  .print-shell {
    box-shadow: none !important;
    border: none !important;
    margin: 0 !important;
    padding: 0 !important;
    max-width: none !important;
    width: 100% !important;
    background: white !important;
  }
  h1, h2 { page-break-after: avoid; break-after: avoid; }
  table, .signatory-card, .items-card, .credit-banner { page-break-inside: avoid; break-inside: avoid; }
}

.print-shell {
  max-width: 820px;
  margin: 24px auto;
  background: white;
  padding: 18mm 16mm 14mm 16mm;
  border: 1px solid var(--rule);
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--ink);
  font-size: 10pt;
  line-height: 1.55;
  font-feature-settings: 'ss01', 'cv11';
}
.print-shell h1, .print-shell h2 {
  font-family: 'Satoshi', 'Inter', sans-serif;
  color: var(--ink);
  margin: 0;
}

.print-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding-bottom: 8mm;
  border-bottom: 1px solid var(--rule);
  margin-bottom: 10mm;
}
.print-header .logo { height: 38px; width: auto; display: block; }
.print-header .doc-meta { text-align: right; font-size: 8pt; color: var(--light); line-height: 1.6; }
.print-header .doc-meta .tag {
  display: inline-block;
  padding: 2px 10px;
  background: var(--rose);
  color: #fff;
  font-size: 8pt; font-weight: 700;
  letter-spacing: 0.22em; text-transform: uppercase;
  border-radius: 2px;
  margin-bottom: 4px;
}
.print-header .doc-meta .num { font-family: 'Satoshi', sans-serif; font-size: 11pt; color: var(--ink); font-weight: 700; letter-spacing: 0.01em; margin-bottom: 1mm; }
.print-header .doc-meta .copy { font-size: 7.5pt; color: var(--light); letter-spacing: 0.14em; text-transform: uppercase; font-weight: 600; margin-top: 1mm; }

.bill-hero { margin-bottom: 8mm; }
.bill-hero .eyebrow {
  font-size: 8pt; font-weight: 600; letter-spacing: 0.18em;
  text-transform: uppercase; color: var(--reedos-orange);
  margin-bottom: 3mm;
}
.bill-hero .client-name {
  font-family: 'Satoshi', sans-serif;
  font-size: 22pt; font-weight: 700;
  color: var(--ink); letter-spacing: -0.02em;
  line-height: 1.15;
  margin-bottom: 3mm;
}
.bill-hero .client-meta { font-size: 9pt; color: var(--muted); line-height: 1.6; max-width: 110mm; }
.bill-hero .client-meta .label { color: var(--light); font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; font-size: 7.5pt; margin-right: 4px; }
.bill-hero .client-meta .mono { font-family: 'Satoshi', sans-serif; font-weight: 600; }
.bill-hero .locked-stamp {
  margin-top: 3mm;
  font-size: 7.5pt;
  color: #047857;
  background: #ECFDF5;
  padding: 1.2mm 2.5mm;
  border-radius: 2px;
  display: inline-block;
  letter-spacing: 0.02em;
  font-weight: 500;
}

/* Credit-banner — the wow callout for credit amount + invoice ref */
.credit-banner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid var(--rose);
  border-top: 3px solid var(--rose);
  border-radius: 4px;
  background: var(--rose-bg);
  margin-bottom: 12mm;
}
.credit-banner .cell {
  padding: 6mm 7mm;
  border-right: 1px solid #FECDD3;
}
.credit-banner .cell:last-child { border-right: 0; }
.credit-banner .lh {
  font-size: 7.5pt; font-weight: 700; letter-spacing: 0.18em;
  text-transform: uppercase; color: var(--rose);
  margin-bottom: 2mm;
}
.credit-banner .amount {
  font-family: 'Satoshi', sans-serif;
  font-size: 22pt; font-weight: 700;
  color: var(--rose); letter-spacing: -0.02em;
  font-feature-settings: 'tnum'; line-height: 1.1;
}
.credit-banner .amount-sub { font-size: 8pt; color: var(--muted); margin-top: 2mm; }
.credit-banner .ref-num {
  font-family: 'Satoshi', sans-serif;
  font-size: 14pt; font-weight: 700;
  color: var(--ink); letter-spacing: -0.01em; line-height: 1.2;
}
.credit-banner .ref-date { font-size: 9pt; color: var(--muted); margin-top: 1.5mm; }
.credit-banner .ref-orig { font-size: 8pt; color: var(--light); margin-top: 1.5mm; letter-spacing: 0.06em; text-transform: uppercase; font-weight: 600; }

/* Reason call-out */
.reason-callout {
  margin-bottom: 12mm;
  border: 1px solid var(--rule);
  border-left: 3px solid var(--rose);
  border-radius: 4px;
  padding: 5mm 6mm;
  background: #FFFAFB;
}
.reason-callout .lh { font-size: 7.5pt; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: var(--rose); margin-bottom: 2mm; }
.reason-callout .reason-label { font-family: 'Satoshi', sans-serif; font-size: 11pt; font-weight: 700; color: var(--ink); letter-spacing: -0.005em; }
.reason-callout .reason-notes { margin-top: 2mm; font-size: 9pt; color: var(--ink); white-space: pre-wrap; line-height: 1.6; }

/* Eyebrow / section */
.eyebrow {
  font-family: 'Inter', sans-serif;
  font-size: 8pt; font-weight: 600; letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--reedos-orange);
  display: inline-flex; align-items: center; gap: 8px;
}
.eyebrow .num {
  font-family: 'Satoshi', sans-serif;
  color: var(--reedos-blue); font-weight: 700;
  font-size: 9pt; letter-spacing: 0.04em;
}
.eyebrow .num::after { content: " /"; color: var(--rule-strong); margin-left: 2px; font-weight: 400; }

.section-title { margin-bottom: 6mm; }
.section-title h2 { font-size: 14pt; font-weight: 700; color: var(--ink); letter-spacing: -0.005em; margin-top: 2mm; }
.section-title .accent-rule { width: 22mm; height: 2px; background: var(--reedos-orange); margin-top: 4mm; }
.section { margin-bottom: 14mm; }

/* Items card */
.items-card { border: 1px solid var(--rule); border-radius: 8px; overflow: hidden; background: #fff; }
.items { width: 100%; border-collapse: collapse; font-size: 9pt; }
.items thead th {
  background: var(--table-header); color: var(--ink);
  font-family: 'Inter', sans-serif; font-weight: 600; font-size: 8pt;
  letter-spacing: 0.04em; text-transform: uppercase;
  padding: 3mm 3.5mm; border-bottom: 1px solid var(--rule);
  text-align: right;
}
.items thead th.text-left { text-align: left; }
.items tbody td {
  padding: 3mm 3.5mm; border-bottom: 1px solid #F3F4F6;
  vertical-align: top; text-align: right;
  font-feature-settings: 'tnum'; font-variant-numeric: tabular-nums;
}
.items tbody td.text-left { text-align: left; }
.items tbody tr:last-child td { border-bottom: 0; }
.items .num-col { width: 9mm; color: var(--light); }
.items .desc { font-weight: 600; color: var(--ink); font-size: 9.5pt; }
.items .figure { font-weight: 500; color: var(--ink); white-space: nowrap; }
.items .figure-em { font-weight: 700; }
.items .gst-pct { display: block; font-size: 7.5pt; color: var(--light); margin-top: 1px; font-weight: 400; }
.items tfoot td {
  background: var(--card-bg);
  border-top: 1px solid var(--rule);
  padding: 2.8mm 3.5mm;
  font-weight: 600; color: var(--ink); font-size: 9pt;
  font-feature-settings: 'tnum'; font-variant-numeric: tabular-nums;
  text-align: right;
}
.items tfoot tr.grand-row td {
  background: var(--rose); color: #fff;
  border-top: 2px solid var(--rose);
  padding: 4.5mm 3.5mm;
  font-weight: 700; font-size: 11pt;
}
.items tfoot tr.grand-row td.label { letter-spacing: 0.18em; text-transform: uppercase; font-size: 9.5pt; }
.items tfoot td.label { text-align: right; letter-spacing: 0.06em; text-transform: uppercase; font-size: 8.5pt; color: var(--ink); font-weight: 600; }

.words-callout {
  margin-top: 4mm;
  padding: 3.5mm 5mm;
  border-left: 2px solid var(--reedos-orange);
  background: #FFF7F4;
  font-size: 9pt; line-height: 1.6; color: var(--ink);
}
.words-callout .lh { font-size: 7.5pt; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: var(--reedos-orange); margin-right: 6px; }

.declaration-block {
  border: 1px solid var(--rule);
  border-top: 3px solid var(--rose);
  border-radius: 4px;
  padding: 5mm 6mm;
  font-size: 9pt; color: var(--muted); line-height: 1.7;
}
.declaration-block .head { font-family: 'Satoshi', sans-serif; font-size: 9.5pt; font-weight: 700; color: var(--ink); margin-bottom: 2mm; }

.signature-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8mm; margin-top: 10mm; }
.signatory-card {
  border: 1px solid var(--rule);
  border-top: 3px solid var(--reedos-blue);
  border-radius: 4px;
  padding: 5mm 5mm 6mm 5mm;
  display: flex; flex-direction: column;
  min-height: 48mm;
}
.signatory-card .for-label { font-size: 7.5pt; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; color: var(--light); margin-bottom: 1.5mm; }
.signatory-card .for-name { font-family: 'Satoshi', sans-serif; font-weight: 700; font-size: 11pt; color: var(--ink); line-height: 1.25; }
.signatory-card .sign-line { margin-top: auto; border-bottom: 1px solid var(--rule-strong); padding-bottom: 1mm; height: 18mm; }
.signatory-card .sign-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 4mm; margin-top: 3mm; font-size: 8pt; color: var(--light); letter-spacing: 0.06em; text-transform: uppercase; font-weight: 600; }

.doc-footer {
  margin-top: 14mm;
  padding-top: 4mm;
  border-top: 1px solid var(--rule);
  display: flex; justify-content: space-between; align-items: center;
  font-size: 8pt; color: var(--light); letter-spacing: 0.02em;
}
.doc-footer .org-line strong { color: var(--ink); font-weight: 700; }
.doc-footer .conf { letter-spacing: 0.18em; text-transform: uppercase; font-weight: 600; color: var(--rose); }
`;

export default function CreditNotePrint() {
    const { id } = useParams();
    const [cn, setCn] = useState(null);
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [c, ci] = await Promise.all([
                    api.get(`/billing/credit-note/${id}`),
                    api.get("/billing/company-info"),
                ]);
                setCn(c.data);
                setCompany(ci.data);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load credit note"));
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    useEffect(() => {
        const prev = document.title;
        if (cn?.cn_number) {
            document.title = `REEDOS Credit Note — ${cn.cn_number}`;
        }
        return () => { document.title = prev; };
    }, [cn?.cn_number]);

    const isIntra = useMemo(() => {
        if (!cn) return false;
        return cn.gst_breakup.cgst > 0 || cn.gst_breakup.sgst > 0;
    }, [cn]);

    // Iter 43.1 — Credit notes inherit tax applicability from the source
    // invoice. International / non-GST sources produce CNs with no GST.
    const gstApplicable = cn ? (cn.gst_applicable !== false) : true;

    if (loading) return <div className="p-10 text-sm text-slate-500">Loading…</div>;
    if (!cn || !company) return <div className="p-10">Credit note not found</div>;

    const placeOfSupply = cn.client?.state_code === company.state_code
        ? company.place_of_supply
        : cn.client?.state_code || company.place_of_supply;

    const sectionEyebrow = (num, label) => (
        <div className="eyebrow"><span className="num">{num}</span>{label}</div>
    );

    return (
        <div className="min-h-screen bg-slate-100" data-testid="cn-print-page">
            <style>{printStyles}</style>

            <div className="no-print bg-white border-b border-slate-200 px-6 py-3 sticky top-0 z-10 flex items-center justify-between">
                <button onClick={() => window.history.back()} className="text-sm text-slate-700 hover:text-slate-900 inline-flex items-center gap-1.5">
                    <ArrowLeft size={14} /> Back
                </button>
                <Button onClick={() => window.print()} data-testid="print-cn-btn" className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5">
                    <Printer size={16} weight="fill" /> Print / Save as PDF
                </Button>
            </div>

            <div className="print-shell shadow-sm">
                {/* Top header bar */}
                <div className="print-header">
                    <img src="/reedos-logo.png" alt="REEDOS" className="logo" />
                    <div className="doc-meta">
                        <div className="tag">Credit Note</div>
                        <div className="num">{cn.cn_number}</div>
                        <div>{formatDate(cn.cn_date)}</div>
                        <div className="copy">Original for Recipient</div>
                    </div>
                </div>

                {/* Credit-to hero */}
                <section className="bill-hero" data-testid="cn-credit-to">
                    <div className="eyebrow">Credit To</div>
                    <div className="client-name">{cn.client?.name || "Client name not set"}</div>
                    <div className="client-meta">
                        {cn.client?.address && <div>{cn.client.address}</div>}
                        <div style={{ marginTop: "1.5mm" }}>
                            {cn.client?.gstin && <><span className="label">GSTIN</span><span className="mono">{cn.client.gstin}</span></>}
                            {cn.client?.gstin && cn.client?.state_code && <span style={{ margin: "0 8px", color: "var(--rule-strong)" }}>·</span>}
                            {cn.client?.state_code && <><span className="label">State</span><span className="mono">{cn.client.state_code}</span></>}
                        </div>
                        {cn.bill_to?.snapshot_at && (
                            <div className="locked-stamp" data-testid="bill-to-locked-stamp">
                                Bill-to fixed as on {formatDate(cn.bill_to.snapshot_at)} — historical record, not affected by later client edits.
                            </div>
                        )}
                    </div>
                </section>

                {/* Credit banner — Total Credit + Original Invoice ref */}
                <div className="credit-banner" data-testid="cn-credit-banner">
                    <div className="cell">
                        <div className="lh">Total Credit</div>
                        <div className="amount">– {formatINR(cn.total_amount, cn?.currency)}</div>
                        <div className="amount-sub">This amount reduces your outstanding against the referenced tax invoice.</div>
                    </div>
                    <div className="cell">
                        <div className="lh">Against Tax Invoice</div>
                        <div className="ref-num">{cn.invoice_number}</div>
                        {cn.invoice?.invoice_date && <div className="ref-date">Originally invoiced on {formatDate(cn.invoice.invoice_date)}</div>}
                        {cn.proposal_number && (
                            <div className="ref-date" data-testid="cn-print-reedos-proposal-no">
                                REEDOS Proposal No · <span style={{ fontFamily: "Geist Mono, monospace" }}>{cn.proposal_number}</span>
                            </div>
                        )}
                        {gstApplicable && <div className="ref-orig">Place of supply · {placeOfSupply}</div>}
                    </div>
                </div>

                {/* Reason call-out */}
                <div className="reason-callout" data-testid="cn-reason">
                    <div className="lh">Reason for Credit</div>
                    <div className="reason-label">{cn.reason_label}</div>
                    {cn.reason_notes && <div className="reason-notes">{cn.reason_notes}</div>}
                </div>

                {/* 01 / Items being credited */}
                <section className="section" data-testid="cn-items-section">
                    <div className="section-title">
                        {sectionEyebrow("01", "Items Being Credited")}
                        <h2>Items Being Credited</h2>
                        <div className="accent-rule" />
                    </div>
                    <div className="items-card">
                        <table className="items">
                            <thead>
                                <tr>
                                    <th className="text-left num-col">#</th>
                                    <th className="text-left">Description</th>
                                    <th className="text-left" style={{ width: "16mm" }}>HSN/SAC</th>
                                    <th style={{ width: "11mm" }}>Qty</th>
                                    <th style={{ width: "22mm" }}>Amount</th>
                                    {gstApplicable && (isIntra ? (
                                        <>
                                            <th style={{ width: "18mm" }}>CGST</th>
                                            <th style={{ width: "18mm" }}>SGST</th>
                                        </>
                                    ) : (
                                        <th style={{ width: "20mm" }}>IGST</th>
                                    ))}
                                    <th style={{ width: "23mm" }}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cn.line_items.map((li, i) => {
                                    const split = isIntra
                                        ? { cgst: li.gst_amount / 2, sgst: li.gst_amount / 2 }
                                        : { igst: li.gst_amount };
                                    return (
                                        <tr key={li.id}>
                                            <td className="text-left num-col">{String(i + 1).padStart(2, "0")}</td>
                                            <td className="text-left"><div className="desc">{li.description}</div></td>
                                            <td className="text-left" style={{ fontFamily: "'Satoshi', sans-serif", color: "var(--muted)", fontSize: "8.5pt" }}>{li.hsn_sac_code || "—"}</td>
                                            <td className="figure">{li.quantity}</td>
                                            <td className="figure">{formatINR(li.amount, cn?.currency)}</td>
                                            {gstApplicable && (isIntra ? (
                                                <>
                                                    <td className="figure">{formatINR(split.cgst, cn?.currency)}<span className="gst-pct">{li.gst_percent / 2}%</span></td>
                                                    <td className="figure">{formatINR(split.sgst, cn?.currency)}<span className="gst-pct">{li.gst_percent / 2}%</span></td>
                                                </>
                                            ) : (
                                                <td className="figure">{formatINR(split.igst, cn?.currency)}<span className="gst-pct">{li.gst_percent}%</span></td>
                                            ))}
                                            <td className="figure figure-em">{formatINR(li.total_amount, cn?.currency)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                {/* C.3 Commit 5 — Decision 2: I-A mirror.
                                    When the source invoice carried a frozen
                                    Special Offer, the CN snapshots a
                                    proportional Standard / Less: Offer
                                    block so the document reconciles to
                                    the same commercial narrative. Reason
                                    code is NEVER printed (parity with
                                    the invoice's I-A rule). */}
                                {cn.special_offer && cn.special_offer.enabled && (
                                    <>
                                        <tr className="subtotal-row" data-testid="cn-special-offer-standard">
                                            <td colSpan={4} className="label">Standard Commercial Value</td>
                                            <td>{formatINR(cn.special_offer.standard_value_at_apply, cn?.currency)}</td>
                                            {gstApplicable && (isIntra ? (<><td></td><td></td></>) : (<td></td>))}
                                            <td></td>
                                        </tr>
                                        <tr className="subtotal-row" data-testid="cn-special-offer-less" style={{ color: "#8a1d2a" }}>
                                            <td colSpan={4} className="label" style={{ color: "#8a1d2a" }}>Less: {cn.special_offer.label}</td>
                                            <td>−{formatINR(cn.special_offer.discount_amount, cn?.currency)}</td>
                                            {gstApplicable && (isIntra ? (<><td></td><td></td></>) : (<td></td>))}
                                            <td></td>
                                        </tr>
                                    </>
                                )}
                                <tr>
                                    <td colSpan={4} className="label">{cn.special_offer?.enabled ? "Taxable Value" : "Subtotal"}</td>
                                    <td>{formatINR(cn.subtotal, cn?.currency)}</td>
                                    {gstApplicable && (isIntra ? (
                                        <>
                                            <td>{formatINR(cn.gst_breakup.cgst, cn?.currency)}</td>
                                            <td>{formatINR(cn.gst_breakup.sgst, cn?.currency)}</td>
                                        </>
                                    ) : (
                                        <td>{formatINR(cn.gst_breakup.igst, cn?.currency)}</td>
                                    ))}
                                    <td className="figure-em">{formatINR(cn.total_amount, cn?.currency)}</td>
                                </tr>
                                <tr className="grand-row">
                                    <td colSpan={!gstApplicable ? 4 : (isIntra ? 5 : 4)} className="label">Total Credit</td>
                                    <td colSpan={!gstApplicable ? 2 : 3}>– {formatINR(cn.total_amount, cn?.currency)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    {cn.amount_in_words && (
                        <div className="words-callout"><span className="lh">Amount in Words</span>{cn.amount_in_words}</div>
                    )}
                </section>

                {/* 02 / Declaration */}
                <section className="section" data-testid="cn-declaration-section">
                    <div className="section-title">
                        {sectionEyebrow("02", "Declaration")}
                        <h2>Declaration</h2>
                        <div className="accent-rule" />
                    </div>
                    <div className="declaration-block">
                        <div className="head">Issued under Indian GST regulations</div>
                        This Credit Note is issued by {company.legal_name} against the tax invoice referenced above. The amount above shall reduce the corresponding GST liability for this period and the Client&apos;s outstanding balance against the said invoice. Both parties shall reflect this Credit Note in their respective GSTR filings for the period of issue.
                    </div>
                </section>

                {/* Signatures */}
                <section className="signature-grid" data-testid="cn-signatures">
                    <div className="signatory-card">
                        <div className="for-label">Acknowledged by (Client)</div>
                        <div className="for-name">{cn.client?.name || "—"}</div>
                        <div className="sign-line" />
                        <div className="sign-meta">
                            <div>Authorised Signatory</div>
                            <div style={{ textAlign: "right" }}>Date</div>
                        </div>
                    </div>
                    <div className="signatory-card">
                        <div className="for-label">For (REEDOS)</div>
                        <div className="for-name">{company.legal_name}</div>
                        <div className="sign-line" />
                        <div className="sign-meta">
                            <div>{company.signatory_designation || "Authorised Signatory"}</div>
                            <div style={{ textAlign: "right" }}>Date</div>
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <div className="doc-footer">
                    <div className="org-line"><strong>{company.legal_name}</strong> · GSTIN {company.gstin}</div>
                    <div className="conf">REEDOS · Credit Note</div>
                </div>
            </div>
        </div>
    );
}
